"use client";

import { highlightParts, type SearchSuggestion } from "./searchMatching";

interface SearchSuggestionsProps {
  id: string;
  suggestions: SearchSuggestion[];
  /** -1 when the box still shows the term as typed. */
  activeIndex: number;
  /** The typed term, used to tint the part of each label that matched. */
  term: string;
  optionId: (index: number) => string;
  onSelect: (suggestion: SearchSuggestion) => void;
}

export default function SearchSuggestions({
  id,
  suggestions,
  activeIndex,
  term,
  optionId,
  onSelect,
}: SearchSuggestionsProps) {
  return (
    <ul
      id={id}
      role="listbox"
      aria-label="Search suggestions"
      // Square and flush against the field, as drawn — the 1px padding is what
      // keeps the last row's edge off the border.
      className="absolute top-full left-0 z-30 w-full max-h-80 p-px overflow-y-auto bg-white border border-black/[0.08] shadow-[0_1.5px_8px_0_rgba(0,0,0,0.2)]">
      {suggestions.map((suggestion, index) => (
        // The row carries role="option" and the handler itself rather than
        // wrapping a button: an ARIA option must not hold interactive content,
        // and the keyboard route in is the combobox input, which owns the arrow
        // and Enter handling and points aria-activedescendant at these ids.
        <li
          key={`${suggestion.kind}-${suggestion.value}`}
          id={optionId(index)}
          role="option"
          aria-selected={index === activeIndex}
          // The input keeps focus throughout, so commit on mousedown: a click
          // would blur the input and close the list before it landed.
          onMouseDown={event => {
            event.preventDefault();
            onSelect(suggestion);
          }}
          // 35px line-height over a 1px bottom inset is what gives the 36px
          // row; long labels wrap to a second line rather than truncating.
          className={`flex items-start w-full pl-[5px] pb-px font-roboto text-base leading-[35px] break-words text-gray-1 border-b border-dashed border-black/[0.08] last:border-b-0 cursor-pointer transition-colors ${
            index === activeIndex ? "bg-[#E7E7E9]" : "hover:bg-[#E7E7E9]"
          }`}>
          <span className="flex-1 min-w-0">
            {highlightParts(suggestion.value, term).map((part, partIndex) => (
              <span key={partIndex} className={part.isMatch ? "text-primary" : undefined}>
                {part.text}
              </span>
            ))}
          </span>
        </li>
      ))}
    </ul>
  );
}
